import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { UploadedFile } from '@atlassian/conversation-assistant-chat-prompt-input/UploadedFile';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createActionsHook,
	type Action,
	type Store,
	type HookFunction,
	type BoundActions,
} from '@atlassian/react-sweet-state';

type State = {
	submittedPrompt: DocNode | null;
	uploadedFiles: UploadedFile[];
	nudgePending: boolean;
	singleInstrumentationId: string | null;
};

const initialState: State = {
	submittedPrompt: null,
	uploadedFiles: [],
	nudgePending: false,
	singleInstrumentationId: null,
};

const actions = {
	setSubmittedPrompt:
		(submittedPrompt: DocNode | null): Action<State> =>
		({ setState }) => {
			setState({ submittedPrompt });
		},
	setSubmittedUploadedFiles:
		(uploadedFiles: UploadedFile[]): Action<State> =>
		({ setState }) => {
			setState({ uploadedFiles });
		},
	setNudgePending:
		(nudgePending: boolean): Action<State> =>
		({ setState }) => {
			setState({ nudgePending });
		},
	setSingleInstrumentationId:
		(singleInstrumentationId: string | null): Action<State> =>
		({ setState }) => {
			setState({ singleInstrumentationId });
		},
	clearSubmittedPrompt:
		(): Action<State> =>
		({ setState }) => {
			setState({
				submittedPrompt: null,
				uploadedFiles: [],
				nudgePending: false,
				singleInstrumentationId: null,
			});
		},
};

type Actions = typeof actions;

export const AiSpaceCreationSubmittedPromptStore: Store<State, Actions> = createStore<
	State,
	Actions
>({
	name: 'aiSpaceCreationSubmittedPromptStore',
	initialState,
	actions,
});

export const useAiSpaceCreationSubmittedPrompt: HookFunction<
	DocNode | null,
	BoundActions<State, Actions>
> = createHook(AiSpaceCreationSubmittedPromptStore, {
	selector: ({ submittedPrompt }: State): DocNode | null => submittedPrompt,
});

export const useAiSpaceCreationUploadedFiles: HookFunction<
	UploadedFile[],
	BoundActions<State, Actions>
> = createHook(AiSpaceCreationSubmittedPromptStore, {
	selector: ({ uploadedFiles }: State): UploadedFile[] => uploadedFiles,
});

export const useAiSpaceCreationNudgePending: HookFunction<
	boolean,
	BoundActions<State, Actions>
> = createHook(AiSpaceCreationSubmittedPromptStore, {
	selector: ({ nudgePending }: State): boolean => nudgePending,
});

export const useAiSpaceCreationSingleInstrumentationId: HookFunction<
	string | null,
	BoundActions<State, Actions>
> = createHook(AiSpaceCreationSubmittedPromptStore, {
	selector: ({ singleInstrumentationId }: State): string | null => singleInstrumentationId,
});

export const useAiSpaceCreationSubmittedPromptActions: () => BoundActions<State, Actions> =
	createActionsHook(AiSpaceCreationSubmittedPromptStore);
